import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { qgApi, errorMessage } from "@/lib/qgApi";
import { computeQuotation } from "@/lib/qgCalc";

const DETAIL_KEYS = ["quotation_number", "client_name", "project_name", "location", "quotation_date", "version_label", "prepared_by",
  "contact_name", "contact_phone", "contact_email", "interior_area_sqft", "interior_rate_sections", "general_notes", "sections"];
const META_KEYS = ["status", "updated_at", "issued_revisions", "revision_number", "version_label", "total_boq"];
const AUTOSAVE_MS = 1500;

let tmp = 0;
export const newItemId = () => `itm_new${Date.now().toString(36)}${(tmp += 1)}`;

// Server-persisted quotation editor state with debounced autosave and explicit Save Draft.
export function useQuotationEditor(id) {
  const [q, setQ] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState({ status: "idle", at: null, error: null });
  const rev = useRef(0);
  const savedRev = useRef(0);
  const timer = useRef(null);
  const inflight = useRef(null);
  const latest = useRef(null);
  latest.current = q;

  useEffect(() => {
    qgApi.get(id).then((doc) => { delete doc.calc; setQ(doc); setSaveState({ status: "saved", at: doc.updated_at, error: null }); })
      .catch((e) => setLoadError(errorMessage(e, "Could not load quotation")));
  }, [id]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    if (inflight.current) await inflight.current.catch(() => {});
    const doc = latest.current;
    if (!doc || doc.status === "final" || rev.current === savedRev.current) return true;
    const sentRev = rev.current;
    const body = Object.fromEntries(DETAIL_KEYS.map((k) => [k, doc[k]]));
    setSaveState((s) => ({ ...s, status: "saving", error: null }));
    const p = qgApi.save(id, body);
    inflight.current = p;
    try {
      const res = await p;
      savedRev.current = Math.max(savedRev.current, sentRev);
      setQ((cur) => ({ ...cur, ...Object.fromEntries(META_KEYS.map((k) => [k, res[k]])) }));
      setSaveState({ status: rev.current === sentRev ? "saved" : "dirty", at: res.updated_at, error: null });
      return true;
    } catch (e) {
      setSaveState((s) => ({ ...s, status: "error", error: errorMessage(e, "Save failed") }));
      return false;
    } finally {
      inflight.current = null;
    }
  }, [id]);

  const mutate = useCallback((fn) => {
    setQ((cur) => {
      if (!cur || cur.status === "final") return cur;
      const next = fn(structuredClone(cur));
      rev.current += 1;
      return next;
    });
    setSaveState((s) => ({ ...s, status: "dirty" }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(), AUTOSAVE_MS);
  }, [save]);

  useEffect(() => {
    const warn = (e) => { if (rev.current !== savedRev.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => { window.removeEventListener("beforeunload", warn); clearTimeout(timer.current); };
  }, []);

  const replace = useCallback((doc) => { delete doc.calc; rev.current = 0; savedRev.current = 0; setQ(doc); setSaveState({ status: "saved", at: doc.updated_at, error: null }); }, []);

  const calc = useMemo(() => (q ? computeQuotation(q) : null), [q]);

  const actions = useMemo(() => {
    const withSection = (key, fn, touch = true) => mutate((d) => {
      const sec = d.sections.find((s) => s.key === key);
      fn(sec, d);
      if (touch) sec.touched = true;
      return d;
    });
    const withGroup = (key, gid, fn) => withSection(key, (sec) => fn(sec.groups.find((g) => g.id === gid), sec));
    return {
      setField: (f, v) => mutate((d) => { d[f] = v; return d; }),
      setSection: (key, patch, touch = false) => withSection(key, (sec) => Object.assign(sec, patch), touch),
      setNotes: (key, notes) => withSection(key, (sec) => { sec.notes = notes; }, false),
      setGroup: (key, gid, patch) => withGroup(key, gid, (g) => Object.assign(g, patch)),
      setItem: (key, gid, iid, patch) => withGroup(key, gid, (g) => Object.assign(g.items.find((i) => i.id === iid), patch)),
      addItem: (key, gid) => withGroup(key, gid, (g) => g.items.push({
        id: newItemId(), sr_no: "", description: "", product_name: "", specification: "", size_capacity: "", brand: "", pieces: "",
        quantity: "", unit: "", dimensions: "", area_sqft: "", rate: "", lump_sum: "", pricing_basis: "qty_rate", remarks: "",
        image_id: null, excluded: false, zero_rate_confirmed: false, area_needs_review: false, area_confirmed: false, package_role: null, custom: true,
      })),
      removeItem: (key, gid, iid) => withGroup(key, gid, (g) => { g.items = g.items.filter((i) => i.id !== iid); }),
      moveItem: (key, gid, iid, dir) => withGroup(key, gid, (g) => {
        const i = g.items.findIndex((x) => x.id === iid);
        const j = i + dir;
        if (j < 0 || j >= g.items.length) return;
        [g.items[i], g.items[j]] = [g.items[j], g.items[i]];
      }),
    };
  }, [mutate]);

  return { q, calc, loadError, saveState, save, actions, replace, readOnly: q?.status === "final" };
}
